import {
  LAYOUTS,
  MENU_SECTIONS,
  MODULE_SETTINGS,
  USER_ROLES,
} from "shared/constants";
import type { AppRouteRecordRaw } from "shared/types";

import { CONVERSATION_ROUTE_NAMES } from "./routeNames";

/**
 * The leerlingoverzicht. The Ingepland/Afgerond split is a route per tab, with the parent
 * redirecting to the first one — the same structure as Taken, Mijn besprekingen and Mijn
 * actieplan. A tab that swaps which gesprekken the page lists is a page, not filter state:
 * the query string here belongs to FilterPanel, which rebuilds it from `selectedFilters`
 * on every apply, and the sidebar highlight reads route records, not query params.
 *
 * The detail and the two voorbereiding routes are children of the overview too, like a
 * taak's `:id`, `:id/create` and `:id/edit` under `/my-tasks`. That is what keeps
 * "Gesprekken" highlighted on them: vue-router marks the menu's router-link active whenever
 * the parent record is in `route.matched`, which a sibling route never is.
 *
 * `settingRequired` and no `moduleRequired`, per CONVERSATION-SETTING-ONLY-GATE-001 —
 * gesprekken are gated on their own school setting and never on a module. The API read
 * path is deliberately ungated, so hiding this menu item is what a school with gesprekken
 * switched off actually sees. Every child repeats the gate: `isAuthorized()` reads
 * `settingRequired` off the deepest matched record only, so a child without it is a way
 * into the overview that the parent's gate never sees.
 */
const routes: AppRouteRecordRaw[] = [
  {
    name: CONVERSATION_ROUTE_NAMES.STUDENT_OVERVIEW,
    path: "/my-conversations",
    component: () => import("views/EmptyView.vue"),
    redirect: { name: CONVERSATION_ROUTE_NAMES.STUDENT_OVERVIEW_PLANNED },
    meta: {
      title: "Mijn gesprekken",
      // No wrapper and no layout title: per the Figma the breadcrumb, the title, the tabs
      // and the filters sit on the page background and only the table is a white card, so
      // the layout's single panel would put a card inside a card. The page renders its own
      // header, the same shape as StudentConversationDetailHeader one click deeper.
      layout: LAYOUTS.MAIN_NO_WRAPPER,
      hideTitle: true,
      roleRequired: [USER_ROLES.STUDENT],
      settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
      isMenuItem: true,
      menuSection: MENU_SECTIONS.MAIN,
      // Above Mijn portfolio (9) and Mijn taken (12), below Mijn feedback and Mijn
      // leerdoelen (both 7). The rest of the student menu keeps its current order; the
      // Figma's full sidebar order is a separate redesign.
      menuOrder: 8,
      // An SVG path, not a `fa`-prefixed name: MenuLink renders the former through an
      // <img> and the latter through QIcon, which is what puts this item's icon in the
      // same visual family as its student-menu neighbours.
      icon: "/images/menu/conversations.svg",
      studentView: true,
    },
    children: [
      {
        name: CONVERSATION_ROUTE_NAMES.STUDENT_OVERVIEW_PLANNED,
        path: "planned",
        component: () =>
          import("conversations/pages/StudentConversationsPage.vue"),
        meta: {
          roleRequired: [USER_ROLES.STUDENT],
          settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
        },
      },
      {
        name: CONVERSATION_ROUTE_NAMES.STUDENT_OVERVIEW_COMPLETED,
        path: "completed",
        component: () =>
          import("conversations/pages/StudentConversationsPage.vue"),
        meta: {
          roleRequired: [USER_ROLES.STUDENT],
          settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
        },
      },
      {
        name: CONVERSATION_ROUTE_NAMES.STUDENT_DETAIL,
        // vue-router ranks the two static tab segments above this param, so
        // `/my-conversations/planned` is the tab and never a gesprek with id "planned".
        path: ":conversationId",
        component: () =>
          import("conversations/pages/StudentConversationDetailPage.vue"),
        meta: {
          title: "Gesprek",
          // The parent is NO_WRAPPER; the detail wants the wrapper but not its white panel.
          layout: LAYOUTS.MAIN,
          roleRequired: [USER_ROLES.STUDENT],
          settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
          // No menu item of its own: this is reached by clicking a row.
          isMenuItem: false,
          // The page renders its own header — breadcrumb, title, badges and date — and its
          // own white cards per block, so the layout's single panel would nest a card in a
          // card.
          hideTitle: true,
          hideBackground: true,
          studentView: true,
        },
      },
      /**
       * "Voorbereiding invullen" and "Voorbereiding bewerken": one page, two routes, the way
       * a taak has `:id/create` and `:id/edit` for "Taak uitvoeren" and "Taak bewerken". The
       * status bar picks one by whether the voorbereiding is already in. They inherit the
       * parent's no-wrapper layout, which is what keeps the form off a white panel. The page
       * carries its own title and buttons, and the tab bar would only distract from the form.
       */
      {
        name: CONVERSATION_ROUTE_NAMES.STUDENT_PREPARE_CREATE,
        path: ":conversationId(\\d+)/prepare/create",
        component: () =>
          import("conversations/pages/StudentConversationPreparePage.vue"),
        meta: {
          title: "Voorbereiding invullen",
          roleRequired: [USER_ROLES.STUDENT],
          settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
          hideSubmenu: true,
          hideTitle: true,
          isMenuItem: false,
          studentView: true,
        },
      },
      {
        name: CONVERSATION_ROUTE_NAMES.STUDENT_PREPARE_EDIT,
        path: ":conversationId(\\d+)/prepare/edit",
        component: () =>
          import("conversations/pages/StudentConversationPreparePage.vue"),
        meta: {
          title: "Voorbereiding bewerken",
          roleRequired: [USER_ROLES.STUDENT],
          settingRequired: [MODULE_SETTINGS.CONVERSATIONS],
          hideSubmenu: true,
          hideTitle: true,
          isMenuItem: false,
          studentView: true,
        },
      },
    ],
  },
];

export default routes;
